import styles from './IA.module.css';

interface NodeProps {
  x: number;
  y: number;
  title: string;
  sub: string;
  small?: boolean;
  root?: boolean;
}

function Node({ x, y, title, sub, small, root }: NodeProps) {
  const w = small ? 140 : 190;
  const offsetY = small ? 26 : 32;
  return (
    <div
      className={`${styles.node} ${root ? styles.nodeRoot : ''} ${small ? styles.nodeSmall : ''}`}
      style={{
        left: x - w / 2,
        top: y - offsetY,
        width: w,
      }}
    >
      <div className={styles.nodeTitle}>{title}</div>
      <div className={styles.nodeSub}>{sub}</div>
    </div>
  );
}

interface RationaleProps {
  n: string;
  title: string;
  body: string;
}

function Rationale({ n, title, body }: RationaleProps) {
  return (
    <div className={styles.rationale}>
      <div className={`row gap-8 ${styles.rationaleHead}`}>
        <span className={`mono ${styles.rationaleN}`}>{n}</span>
        <h3 className={styles.rationaleTitle}>{title}</h3>
      </div>
      <p className={styles.rationaleBody}>{body}</p>
    </div>
  );
}

const CONNECTIONS: Array<[number, number, number, number]> = [
  [680, 360, 220, 180],
  [680, 360, 1140, 180],
  [680, 360, 220, 560],
  [680, 360, 1140, 560],
  [680, 360, 230, 360],
  [680, 360, 1130, 360],
  [680, 360, 460, 660],
  [680, 360, 900, 660],
  [680, 360, 460, 60],
  [680, 360, 900, 60],
  [230, 360, 230, 460],
  [1140, 180, 1140, 480],
  [220, 180, 1100, 320],
];

export function IA() {
  return (
    <div className={`bv ${styles.root}`}>
      <div className={`row ${styles.headerRow}`}>
        <div>
          <div className={`eyebrow ${styles.eyebrow}`}>Designnota · 01</div>
          <h1 className={styles.title}>Informatie-architectuur</h1>
          <p className={styles.intro}>
            Tien kern-entiteiten. <em>Fase</em> is het anker — bijna alles zit er via een
            many-to-many relatie aan vast. De gebruiker navigeert primair langs de tijdlijn, en
            springt vanuit elke entiteit naar de gerelateerde ervan.
          </p>
        </div>
        <div className={`ml-auto col gap-4 ${styles.headerMeta}`}>
          <div>v0.3 · interne documentatie</div>
        </div>
      </div>

      <div className={styles.graph}>
        <svg viewBox="0 0 1360 720" className={styles.svg} aria-hidden="true">
          <defs>
            <marker
              id="ah"
              viewBox="0 0 10 10"
              refX="9"
              refY="5"
              markerWidth="6"
              markerHeight="6"
              orient="auto"
            >
              <path d="M0,0 L10,5 L0,10 z" fill="oklch(70% 0.012 60)" />
            </marker>
          </defs>
          {CONNECTIONS.map(([x1, y1, x2, y2]) => (
            <line
              key={`${x1}-${y1}-${x2}-${y2}`}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke="oklch(82% 0.014 80)"
              strokeWidth="1"
              strokeDasharray="3 4"
              markerEnd="url(#ah)"
            />
          ))}
        </svg>

        <Node x={680} y={360} title="Project" sub="overkoepelend" root />

        <div className={styles.fase}>
          <div className={styles.faseEyebrow}>Anchor</div>
          <div className={styles.faseTitle}>Fase</div>
          <div className={styles.faseSub}>
            10 hoofd-fases · sub-stappen
            <br />
            kritiek pad · afhankelijkheden
          </div>
        </div>

        <Node x={220} y={180} title="Document" sub="versies, tags, preview" />
        <Node x={1140} y={180} title="Contact" sub="rol, comm-history" />
        <Node x={220} y={560} title="Mail" sub="in/uit · template" />
        <Node x={1140} y={560} title="Taak" sub="deadline · verantw." />
        <Node x={230} y={360} title="Budgetpost" sub="voorzien vs werkelijk" />
        <Node x={1130} y={360} title="Foto" sub="datum · plan-tag" />
        <Node x={460} y={660} title="Beslissing" sub="motivatie · opties" />
        <Node x={900} y={660} title="Werfverslag" sub="acties → taken" />
        <Node x={460} y={60} title="Sub-stap" sub="checklist binnen fase" small />
        <Node x={900} y={60} title="Mijlpaal" sub="datum-anker" small />
        <Node x={230} y={460} title="Factuur" sub="BTW · betaalstatus" small />

        <div className={styles.legend}>
          <div className="row gap-6">
            <span className={styles.legendDashed} /> many-to-many relatie
          </div>
          <div className="row gap-6">
            <span className={styles.legendCentral} /> centrale entiteit
          </div>
          <div className="row gap-6">
            <span className={styles.legendSatellite} /> satelliet-entiteit
          </div>
        </div>
      </div>

      <div className={`row gap-24 ${styles.rationales}`}>
        <Rationale
          n="A"
          title="Fase als anker"
          body="Alles wat in een bouwproject gebeurt, hoort tot één of meer fases. Door Fase centraal te zetten kan de gebruiker vanuit elk scherm één tab terug naar context."
        />
        <Rationale
          n="B"
          title="Many-to-many overal"
          body="Een document hoort bij 'vergunning' én 'ruwbouw'. Een contact werkt op 3 fases. We dwingen geen 1-op-1 af; UI moet meerdere koppelingen zichtbaar maken."
        />
        <Rationale
          n="C"
          title="Spring-navigatie"
          body="Vanuit elke entiteit-detail-view: 'gerelateerde fases', 'gekoppelde contacten', 'documenten'. Broodkruimels tonen het pad terug."
        />
      </div>
    </div>
  );
}
